import {z} from "zod";
import _ from "lodash";
import {zodResolver} from "@hookform/resolvers/zod";
import {UseFormReturn, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {Grid} from "@mui/material";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {
    PatchCompanyBodyType,
    PostCompanyBodyType,
    createCompany,
    deleteCompany,
    patchCompany,
} from "../../services/companyService";
import {Company} from "../../types/domain/company";
import {Dialog} from "../base/Dialog";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {CompanyInfoForm, CompanyInfoFormInputs} from "../companies/CompanyInfoForm";
import {CompanyAdminPreferencesForm, CompanyAdminPreferencesFormInputs} from "../companies/CompanyAdminPreferencesForm";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {useAvailableParentCompanies} from "../../hooks/companiesHooks";
import {CompanyIcon} from "../icons/CompanyIcon";
import {useQueryClient} from "@tanstack/react-query";

export interface Props {
    mode: "create" | "edit";
    company?: Company;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const CompanyEditDialog: React.FC<Props> = ({company, open, onClose, mode}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const companyManagement = useResourceManagement({createCompany, patchCompany, deleteCompany});
    const initFormValues = mode === "edit" ? companyToFormInputs(company!) : NEW_COMPANY_FORM_INPUTS;
    const availableParentCompanies = useAvailableParentCompanies();

    const form = useForm({
        defaultValues: initFormValues,
        resolver: zodResolver(COMPANY_EDIT_SCHEMA),
    });

    const onSubmit = (formInputs: FormInputs) => {
        companyManagement.reset();
        if (mode === "edit") {
            companyManagement
                .patchCompany(
                    company!.id,
                    formInputsToPatchCompanyBody(formInputs, access.canSetAdvancedCompanyProperties),
                )
                .then(() => {
                    void queryClient.invalidateQueries({queryKey: ["company", company!.id]});
                    void queryClient.invalidateQueries({queryKey: ["parentCompany", company!.id]});
                    onClose(true);
                })
                .catch(swallowException);
        } else {
            companyManagement
                .createCompany(formInputsToPostCompanyBody(formInputs), {claimEditorRole: true})
                .then(() => {
                    void queryClient.invalidateQueries({queryKey: ["company", company!.id]});
                    void queryClient.invalidateQueries({queryKey: ["parentCompany", company!.id]});
                    onClose(true);
                })
                .catch(swallowException);
        }
    };

    const onDelete = () => {
        companyManagement.reset();

        companyManagement
            .deleteCompany(company!.id)
            .then(() => {
                void queryClient.invalidateQueries({queryKey: ["company", company!.id]});
                void queryClient.invalidateQueries({queryKey: ["parentCompany", company!.id]});
                onClose(true);
            })
            .catch(swallowException);
    };

    return (
        <Dialog
            enableSubmit
            enableDelete={mode === "edit"}
            deleteTitle={t("dialogs.actions.delete")}
            submitTitle={t("dialogs.actions.save")}
            title={mode === "edit" ? t("dialogs.company_edit.edit_title") : t("dialogs.company_edit.create_title")}
            titleIcon={
                <CompanyIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            open={open}
            error={getResourceErrorMessage(companyManagement.resource)}
            deleteConfirmation={t("dialogs.company_edit.delete_confirmation")}
            onClose={() => onClose(false)}
            onDelete={onDelete}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CompanyInfoForm
                        initFormValues={initFormValues}
                        form={form as unknown as UseFormReturn<CompanyInfoFormInputs>}
                        companies={availableParentCompanies.isSuccess ? availableParentCompanies.data : []}
                        isCompaniesLoading={availableParentCompanies.isLoading}
                        enableParentCompanySelection={access.canSetParentCompany}
                    />
                </Grid>
                {access.canSetAdvancedCompanyProperties && (
                    <Grid item xs={12}>
                        <CompanyAdminPreferencesForm
                            initFormValues={initFormValues}
                            form={form as unknown as UseFormReturn<CompanyAdminPreferencesFormInputs>}
                        />
                    </Grid>
                )}
            </Grid>
        </Dialog>
    );
};

interface FormInputs extends CompanyInfoFormInputs, CompanyAdminPreferencesFormInputs {}

const COMPANY_EDIT_SCHEMA = z
    .discriminatedUnion("hasNoAddress", [
        z.object({
            hasNoAddress: z.literal(true),
            name: z.string().min(1).max(200),
            isReseller: z.boolean(),
            isApiEnabled: z.boolean(),
            includeChildCompanies: z.boolean(),
            parentCompany: z.any(),
            vat: z.string(),
            invoiceEmail: z.union([z.string().email(), z.string().length(0)]),
            country: z.string(),
            phone: z.string(),
        }),
        z.object({
            hasNoAddress: z.literal(false),
            name: z.string().min(1).max(200),
            isReseller: z.boolean(),
            isApiEnabled: z.boolean(),
            includeChildCompanies: z.boolean(),
            parentCompany: z.any(),
            vat: z.string(),
            invoiceEmail: z.union([z.string().email(), z.string().length(0)]),
            address: z.string().min(1).max(200),
            city: z.string().min(1).max(190),
            state: z.string().max(100).optional(),
            country: z.string(),
            postalCode: z.string().min(1).max(20),
            phone: z.string(),
        }),
    ])
    .refine((data) => data.hasNoAddress || !_.isEmpty(data.country), {
        message: "Select a country",
        path: ["country"],
    });

const formInputsToPatchCompanyBody = (
    inputs: FormInputs,
    canSetAdvancedCompanyProperties: boolean,
): PatchCompanyBodyType => {
    return {
        properties: {
            name: inputs.name,
            vat: _.isEmpty(inputs.vat) ? null : inputs.vat,
            invoiceEmail: _.isEmpty(inputs.invoiceEmail) ? null : inputs.invoiceEmail,
            address: inputs.hasNoAddress
                ? null
                : {
                      address: inputs.address,
                      city: inputs.city,
                      state: _.isEmpty(inputs.state) ? null : inputs.state,
                      country: inputs.country,
                      postalCode: inputs.postalCode,
                  },
            phone: _.isEmpty(inputs.phone) ? null : inputs.phone,
        },
        parentCompanyId: inputs.parentCompany,
        ...(canSetAdvancedCompanyProperties && {
            isReseller: inputs.isReseller,
            isApiEnabled: inputs.isApiEnabled,
            includeChildCompanies: inputs.includeChildCompanies,
        }),
    };
};

const formInputsToPostCompanyBody = (inputs: FormInputs): PostCompanyBodyType => ({
    properties: {
        name: inputs.name,
        vat: _.isEmpty(inputs.vat) ? null : inputs.vat,
        invoiceEmail: _.isEmpty(inputs.invoiceEmail) ? null : inputs.invoiceEmail,
        address: inputs.hasNoAddress
            ? null
            : {
                  address: inputs.address,
                  city: inputs.city,
                  state: _.isEmpty(inputs.state) ? null : inputs.state,
                  country: inputs.country,
                  postalCode: inputs.postalCode,
              },
        phone: _.isEmpty(inputs.phone) ? null : inputs.phone,
    },
    parentCompanyId: inputs.parentCompany,
    isReseller: inputs.isReseller,
    isApiEnabled: inputs.isApiEnabled,
});

const companyToFormInputs = (company: Company): FormInputs => ({
    name: company.properties.name,
    isReseller: company.isReseller,
    isApiEnabled: company.isApiEnabled,
    includeChildCompanies: company.includeChildCompanies,
    parentCompany: company.parentCompanyId,
    vat: company.properties.vat ?? "",
    invoiceEmail: company.properties.invoiceEmail ?? "",
    address: company.properties.address?.address ?? "",
    city: company.properties.address?.city ?? "",
    state: company.properties.address?.state ?? "",
    country: company.properties.address?.country ?? "",
    postalCode: company.properties.address?.postalCode ?? "",
    phone: company.properties.phone ?? "",
    hasNoAddress: company.properties.address === null,
});

const NEW_COMPANY_FORM_INPUTS: FormInputs = {
    name: "",
    isReseller: false,
    isApiEnabled: false,
    includeChildCompanies: false,
    parentCompany: null,
    vat: "",
    invoiceEmail: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    phone: "",
    hasNoAddress: false,
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, [
    "canSetAdvancedCompanyProperties",
    "canSetParentCompany",
    "canGetAllCompanies",
]);
